import gql from 'graphql-tag';
import deviceFragment from '../fragments/device';
import userRolesFragment from '../fragments/userRoles';
import workspaceFragment from '../fragments/workspace';

// Superadmin lookup for a workspace the user holds no user_role in.
// getAllWorkspaces is gated by workspace.view at system scope, so this is the
// real enforcement point — a non-superadmin gets FORBIDDEN here.
// `systemId` is accepted by the schema but ignored by workspaceService; passed
// for consistency with the other getAllWorkspaces call sites.
const GET_WORKSPACE_BY_ID = gql`
  query getWorkspaceByIdForSuperAdmin($id: ID!) {
    getAllWorkspaces(systemId: 1, filter: { id: { eq: $id } }, take: 1) {
      workspaces {
        ...workspaceDetails
      }
    }
  }
  ${workspaceFragment.FRAGMENT_WORKSPACE}
`;

const GET_DEVICES_BY_WORKSPACE = gql`
  query getDevicesByWorkspace(
    $workspaceId: ID!
    $filter: DeviceFilterInput
    $sort: DeviceSortInput
    $skip: Int
    $take: Int
    $includeSharedWorkspaces: Boolean = false
  ) {
    getDevicesByWorkspace(
      workspaceId: $workspaceId
      filter: $filter
      sort: $sort
      skip: $skip
      take: $take
      includeSharedWorkspaces: $includeSharedWorkspaces
    ) {
      devices {
        ...deviceSummary
      }
      aggregate {
        rows
      }
    }
  }
  ${deviceFragment.FRAGMENT_DEVICE_SUMMARY}
`;

const GET_INVITATIONS_BY_WORKSPACE = gql`
  query getInvitationsByWorkspaceId(
    $workspaceId: ID!
    $filter: InvitationFilterInput
    $sort: InvitationSortInput
    $skip: Int
    $take: Int
  ) {
    getInvitationsByWorkspaceId(
      workspaceId: $workspaceId
      filter: $filter
      sort: $sort
      skip: $skip
      take: $take
    ) {
      invitations {
        id
        email
        status
        workspaceId
      }
      aggregate {
        rows
      }
    }
  }
`;

const GET_USERS_BY_WORKSPACE = gql`
  query getUsersByWorkspaceId($workspaceId: ID!) {
    getUsersByWorkspaceId(workspaceId: $workspaceId) {
      users {
        id
        firstName
        lastName
        email
        currentWorkspace
        phone
        jobTitle
        language
        timezone
        profilePicture
        config
        userRoles {
          ...userRoleDetails
        }
        createdAt
        updatedAt
      }
    }
  }
  ${userRolesFragment.FRAGMENT_USER_ROLES}
`;

const GET_BRAND_BY_WORKSPACE_ID = gql`
  query GetBrandsByWorkspaceId($workspaceId: ID!) {
    getBrandsByWorkspaceId(workspaceId: $workspaceId) {
      brands {
        id
        name
        website
        workspaceId
        isDefault
        custom {
          id
          name
          website
          workspaceId
        }
      }
    }
  }
`;

const GET_MODEL_BY_WORKSPACE_ID = gql`
  query GetModelsByWorkspaceId($workspaceId: ID!) {
    getModelsByWorkspaceId(workspaceId: $workspaceId) {
      models {
        id
        type
        name
        workspaceId
        isDefault
        custom {
          id
          name
          type
          brandId
        }
        brand {
          id
          name
        }
      }
    }
  }
`;

const GET_EVENT_BY_WORKSPACE_ID = gql`
  query getEventsByWorkspaceId($workspaceId: ID!) {
    getEventsByWorkspaceId(workspaceId: $workspaceId) {
      events {
        id
        code
        technicalDescription
        protocol {
          id
          name
        }
        type
        priority
        workspaceId
        isDefault
        isMonitorable
        color
        eventGroup {
          id
          name
        }
        custom {
          id
          code
          technicalDescription
          protocolId
          type
          priority
          eventGroupId
          color
          isMonitorable
        }
      }
    }
  }
`;

const GET_EVENT_BY_WORKSPACE_ID_AND_PROTOCOL_ID = gql`
  query getEventsByWorkspaceIdAndProtocolId(
    $workspaceId: ID!
    $protocolId: ID!
  ) {
    getEventsByWorkspaceIdAndProtocolId(
      workspaceId: $workspaceId
      protocolId: $protocolId
    ) {
      events {
        id
        code
        technicalDescription
        protocol {
          id
          name
        }
        type
        priority
        workspaceId
        isDefault
        color
        isMonitorable
        eventGroup {
          id
          name
        }
        custom {
          id
          code
          technicalDescription
          protocolId
          type
          priority
          eventGroupId
          isMonitorable
          color
        }
      }
    }
  }
`;

const GET_DEVICES_BY_WORKSPACE_FOR_REPORTS = gql`
  query getDevicesByWorkspaceForReports(
    $workspaceId: ID!
    $filter: DeviceFilterInput
    $sort: DeviceSortInput
    $skip: Int
    $take: Int
    $includeSharedWorkspaces: Boolean = false
    $deviceHistoriesCountFilter: DeviceHistoryFilterInput
  ) {
    getDevicesByWorkspace(
      workspaceId: $workspaceId
      filter: $filter
      sort: $sort
      skip: $skip
      take: $take
      includeSharedWorkspaces: $includeSharedWorkspaces
    ) {
      devices {
        ...deviceReports
      }
      aggregate {
        rows
      }
    }
  }
  ${deviceFragment.FRAGMENT_DEVICE_REPORTS}
`;

export default {
  GET_WORKSPACE_BY_ID,
  GET_DEVICES_BY_WORKSPACE,
  GET_DEVICES_BY_WORKSPACE_FOR_REPORTS,
  GET_INVITATIONS_BY_WORKSPACE,
  GET_USERS_BY_WORKSPACE,
  GET_BRAND_BY_WORKSPACE_ID,
  GET_MODEL_BY_WORKSPACE_ID,
  GET_EVENT_BY_WORKSPACE_ID,
  GET_EVENT_BY_WORKSPACE_ID_AND_PROTOCOL_ID,
};
